import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, MessageSquare, CheckSquare, FolderKanban, Info, Loader2, Award, AlertOctagon, Copy, Check } from 'lucide-react';
import { api } from '@/lib/api.js';

export interface NotificationItem {
  id: string;
  user_id?: string;
  title: string;
  message: string;
  type?: 'assignment' | 'mention' | 'project' | 'task_assigned' | 'task_completed' | 'info' | 'milestone_completed' | 'blocker_alert';
  link?: string;
  read: boolean;
  created_at: string;
  handover_summary?: string;
}

export function NotificationCenter() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleCopySummary = async (notif: NotificationItem) => {
    const summary = notif.handover_summary || `${notif.title}\n${notif.message}\nStatus: 100% Completed & Verified for Client Handover.`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(summary);
      } else {
        const ta = document.createElement('textarea');
        ta.value = summary;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedId(notif.id);
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      // Fallback
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get<{ data: NotificationItem[] }>('/api/v1/notifications');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setNotifications(list);
    } catch {
      // Fallback
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Polling every 30 seconds
    const handleRefresh = () => fetchNotifications();
    window.addEventListener('tsos_refresh_alerts', handleRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('tsos_refresh_alerts', handleRefresh);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Mark notifications as read on opening
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      const timer = setTimeout(() => {
        handleMarkAllRead();
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, unreadCount]);

  const handleMarkAllRead = async () => {
    setIsLoading(true);
    try {
      await api.patch('/api/v1/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } finally {
      setIsLoading(false);
      window.dispatchEvent(new CustomEvent('tsos_refresh_alerts'));
    }
  };

  const handleItemClick = async (notif: NotificationItem) => {
    if (!notif.read) {
      try {
        await api.patch(`/api/v1/notifications/${notif.id}/read`);
      } catch {}
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
      );
      window.dispatchEvent(new CustomEvent('tsos_refresh_alerts'));
    }
    setIsOpen(false);
    if (notif.link) {
      const cleanLink = notif.link.replace(/^#/, '').replace(/^\/#/, '');
      navigate(cleanLink.startsWith('/') ? cleanLink : `/${cleanLink}`);
    }
  };

  const getIcon = (type?: string) => {
    switch (type) {
      case 'blocker_alert':
        return <AlertOctagon className="h-4 w-4 text-red-600 dark:text-red-400" />;
      case 'milestone_completed':
        return <Award className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />;
      case 'mention':
        return <MessageSquare className="h-4 w-4 text-brand" />;
      case 'assignment':
      case 'task_assigned':
      case 'task_completed':
        return <CheckSquare className="h-4 w-4 text-emerald-600" />;
      case 'project':
        return <FolderKanban className="h-4 w-4 text-amber-600" />;
      default:
        return <Info className="h-4 w-4 text-brand-muted" />;
    }
  };

  const hasUnreadBlocker = notifications.some((n) => !n.read && n.type === 'blocker_alert');
  const hasUnreadMilestone = notifications.some((n) => !n.read && n.type === 'milestone_completed');

  const formatTime = (isoString?: string) => {
    if (!isoString) return 'recently';
    try {
      const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
      if (diff < 60) return 'just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    } catch {
      return 'recently';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative p-2 text-brand-muted hover:text-brand-ink transition-colors rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
        title="Notifications"
        aria-label="Open notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className={`absolute top-2 right-2 h-4 min-w-[16px] px-1 rounded-full font-bold text-[10px] flex items-center justify-center ring-2 ring-white dark:ring-slate-900 shadow-sm animate-pulse ${
            hasUnreadBlocker
              ? 'bg-rose-600 text-white'
              : hasUnreadMilestone
              ? 'bg-emerald-600 text-white'
              : 'bg-brand-lime text-brand-ink'
          }`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown Flyout */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-xl bg-white dark:bg-[#12211F] shadow-2xl border border-brand-border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="flex items-center justify-between px-4 py-3 border-b border-brand-border bg-brand-surface dark:bg-[#0E1A18]">
            <div className="flex items-center space-x-2">
              <span className="text-xs font-bold text-brand-ink">Notifications</span>
              {unreadCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  hasUnreadBlocker
                    ? 'bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-300'
                    : hasUnreadMilestone
                    ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300'
                    : 'bg-brand-tint text-brand'
                }`}>
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={isLoading}
                className="text-[11px] font-medium text-brand hover:text-brand-hover flex items-center space-x-1"
              >
                {isLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <CheckCheck className="h-3.5 w-3.5" />
                )}
                <span>Mark all read</span>
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto divide-y divide-brand-border">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-xs text-brand-muted space-y-1">
                <Bell className="h-6 w-6 mx-auto text-brand-border mb-2" />
                <p className="font-semibold text-brand-ink">All caught up!</p>
                <p className="text-[11px]">No notifications right now.</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleItemClick(notif)}
                  className={`p-3.5 transition-colors cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 flex items-start space-x-3 ${
                    !notif.read
                      ? notif.type === 'blocker_alert'
                        ? 'bg-red-50/50 dark:bg-red-950/20 border-l-2 border-red-500'
                        : notif.type === 'milestone_completed'
                        ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-l-2 border-emerald-500'
                        : 'bg-brand-tint/20 dark:bg-emerald-950/10'
                      : ''
                  }`}
                >
                  <div className={`p-2 rounded-lg flex-shrink-0 mt-0.5 ${
                    notif.type === 'blocker_alert'
                      ? 'bg-red-100 dark:bg-red-950/60'
                      : notif.type === 'milestone_completed'
                      ? 'bg-emerald-100 dark:bg-emerald-950/60'
                      : 'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    {getIcon(notif.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h5 className="text-xs font-bold text-brand-ink truncate">{notif.title}</h5>
                      <span className="text-[10px] text-brand-muted flex-shrink-0 ml-2">
                        {formatTime(notif.created_at)}
                      </span>
                    </div>
                    <p className="text-[11px] text-brand-muted mt-0.5 line-clamp-2 leading-relaxed">
                      {notif.message}
                    </p>
                    {notif.type === 'milestone_completed' && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopySummary(notif);
                          }}
                          className="inline-flex items-center space-x-1 px-2 py-1 rounded bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-300 text-[10px] font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
                          title="Copy client handover summary to clipboard"
                        >
                          {copiedId === notif.id ? (
                            <>
                              <Check className="h-3 w-3 text-emerald-600" />
                              <span>Handover Summary Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3 text-emerald-600" />
                              <span>1-Click Client Summary</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  {!notif.read && (
                    <span className={`h-2 w-2 rounded-full flex-shrink-0 self-center ${
                      notif.type === 'blocker_alert'
                        ? 'bg-red-500'
                        : notif.type === 'milestone_completed'
                        ? 'bg-emerald-500'
                        : 'bg-brand'
                    }`} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
